import { Container, EventDispatcher, EventStream, Platform, T_Platform } from '@cgs/wge';
import { IStorageMarshalService, STORAGE_MARSHAL_SERVICE_TOKEN } from '@cgs/shared';

export const APP_SETTINGS_SERVICE_TOKEN = Symbol('AppSettingsService');

export interface AppSettings {
  sound: number;
}

export interface IAppSettingsService<T extends AppSettings> {
  onSettingsChanged: EventStream<T>;
}

export class AppSettingsService<
  T extends AppSettings = AppSettings,
> implements IAppSettingsService<T> {
  private readonly settingsChangedDispatcher = new EventDispatcher<T>();
  private readonly settingsKey: string = 'settings';
  private readonly soundKey: string = 'sound';

  protected settings: T = {
    sound: 0.5,
  } as T;

  public readonly onSettingsChanged: EventStream<T> = this.settingsChangedDispatcher.eventStream;

  private readonly storageMarshalService: IStorageMarshalService;

  private readonly platform: Platform;

  constructor(container: Container) {
    this.platform = container.resolve<Platform>(T_Platform);
    this.storageMarshalService = container.resolve<IStorageMarshalService>(
      STORAGE_MARSHAL_SERVICE_TOKEN
    );

    const sound = this.getSound();

    this.settings = {
      sound,
    } as T;

    this.platform.audioSystem.audioDevice.globalVolume = Math.min(Math.max(sound, 0), 1);
  }

  public get sounds(): number {
    return this.getSound();
  }

  public set sounds(value: number) {
    this.setSound(value);

    this.settingsChangedDispatcher.dispatchEvent(this.settings);
  }

  private setSound(value: number): void {
    this.storageMarshalService.item = {
      value: value.toString(),
      key: `${this.settingsKey}.${this.soundKey}`,
    };

    this.settings.sound = value;

    this.platform.audioSystem.audioDevice.globalVolume = Math.min(Math.max(value, 0), 1);
  }

  private getSound(): number {
    const sound = this.storageMarshalService.item.number(`${this.settingsKey}.${this.soundKey}`);
    return sound !== null && !isNaN(sound) ? sound : this.settings.sound;
  }
}
